import React from 'react';
import { motion } from 'framer-motion';
import { useFirestore } from '../hooks/useFirestore';
import StatsCard from '../components/Dashboard/StatsCard';
import QuickNav from '../components/Navigation/QuickNav';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiFileText, FiCoins, FiGlobe, FiDatabase, FiCloud, FiAlertTriangle } = FiIcons;

const Dashboard = () => {
  const { documents: agencies, loading: agenciesLoading, error: agenciesError, useLocalStorage: agenciesLocal } = useFirestore('agencies');
  const { documents: articles, loading: articlesLoading, error: articlesError, useLocalStorage: articlesLocal } = useFirestore('articles');
  const { documents: domains, loading: domainsLoading, error: domainsError, useLocalStorage: domainsLocal } = useFirestore('domains');

  const loading = agenciesLoading || articlesLoading || domainsLoading;
  const error = agenciesError || articlesError || domainsError;
  const usingLocalStorage = agenciesLocal || articlesLocal || domainsLocal;

  // Calculate statistics
  const totalAgencies = agencies.length;
  const totalArticles = articles.length;
  const totalDomains = domains.length;
  const totalEarnings = articles.reduce((sum, article) => sum + (article.price || 0), 0);

  // Article statistics
  const publishedArticles = articles.filter(article => article.status === 'published').length;
  const completedArticles = articles.filter(article => article.status === 'completed').length;
  const inProgressArticles = articles.filter(article => article.status === 'in_progress').length;
  const draftArticles = articles.filter(article => article.status === 'draft').length;

  // Recent articles
  const recentArticles = articles
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard Overview</h1>
            <p className="text-gray-600">Monitor your article management and earnings</p>
          </div>
          <div className="flex items-center space-x-2">
            {usingLocalStorage ? (
              <>
                <SafeIcon icon={FiDatabase} className="w-5 h-5 text-warning-500" />
                <span className="text-sm text-warning-600">Using Local Storage</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiCloud} className="w-5 h-5 text-success-500" />
                <span className="text-sm text-success-600">Firebase Connected</span>
              </>
            )}
          </div>
        </div>
        
        {/* Firebase Permissions Warning */}
        {usingLocalStorage && error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 bg-warning-50 border border-warning-200 rounded-lg"
          >
            <div className="flex items-start space-x-3">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-warning-600 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-warning-800">Firebase Permissions Issue</h4>
                <p className="text-sm text-warning-700 mt-1">
                  {error.includes('permissions') ? (
                    <>
                      Firebase Firestore permissions are blocking operations. 
                      <a 
                        href="https://console.firebase.google.com/project/cookieguard-pro-1fd39/firestore/rules" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="underline hover:text-warning-900 ml-1"
                      >
                        Update your Firestore rules
                      </a> to allow read/write operations.
                    </>
                  ) : (
                    error
                  )}
                </p>
                <div className="mt-2 text-xs text-warning-600">
                  Currently using browser storage as fallback. Data is saved locally.
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Quick Navigation */}
      <QuickNav />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
        <StatsCard
          title="Total Agencies"
          value={totalAgencies}
          icon={FiUsers}
          color="blue"
        />
        <StatsCard
          title="Total Articles"
          value={totalArticles}
          icon={FiFileText}
          color="green"
        />
        <StatsCard
          title="Total Domains"
          value={totalDomains}
          icon={FiGlobe}
          color="purple"
        />
        <StatsCard
          title="Total Earnings"
          value={`${totalEarnings.toFixed(2)} LEI`}
          icon={FiCoins}
          color="indigo"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Published Articles"
          value={publishedArticles}
          icon={FiFileText}
          color="green"
        />
        <StatsCard
          title="Completed Articles"
          value={completedArticles}
          icon={FiFileText}
          color="blue"
        />
        <StatsCard
          title="In Progress"
          value={inProgressArticles}
          icon={FiFileText}
          color="yellow"
        />
        <StatsCard
          title="Draft Articles"
          value={draftArticles}
          icon={FiFileText}
          color="red"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Articles</h3>
          <div className="space-y-4">
            {recentArticles.length === 0 ? (
              <p className="text-gray-500 text-sm">No articles yet. Add your first article to get started!</p>
            ) : (
              recentArticles.map((article) => (
                <div key={article.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-primary-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{article.title}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(article.created_at).toLocaleDateString()} • {(article.price || 0).toFixed(2)} LEI
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <a
              href="#/articles"
              className="block w-full p-3 text-left bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-primary-800">Add New Article</div>
              <div className="text-xs text-primary-600">Create and track a new article</div>
            </a>
            <a
              href="#/agencies"
              className="block w-full p-3 text-left bg-success-50 hover:bg-success-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-success-800">Add New Agency</div>
              <div className="text-xs text-success-600">Register a new client agency</div>
            </a>
            <a
              href="#/domains"
              className="block w-full p-3 text-left bg-warning-50 hover:bg-warning-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-warning-800">Add New Domain</div>
              <div className="text-xs text-warning-600">Register a publication domain</div>
            </a>
            <a
              href="#/earnings"
              className="block w-full p-3 text-left bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
            >
              <div className="text-sm font-medium text-purple-800">View Earnings</div>
              <div className="text-xs text-purple-600">Check your earnings analytics</div>
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;